import * as React from "react";

import {Types}                           from "@Common/types";
import {cs}                              from "@Common/react/chain-services";
import {consumeContext, provideContext1} from "@Common/react/context";
import {fragments}                       from "@Common/react/fragments";
import {Invoke}                          from "@Common/react/invoke";
import {Load}                            from "@Common/react/load";
import {Static2}                         from "@Common/react/static-2";
import {UseState}                        from "@Common/react/use-state";
import {changeNestedValue}               from "@Common/utils/arr-path";

import {TabGuard, isTabGuardEnabled} from "./tab-guard";

export const loadSession = ({company, userLevels, next}) => cs(
    consumeContext("apis"),
    ["state", (_, next) => UseState({
        initValue: {}, // {path}
        next,
    })],
    ["session", ({state, apis}, next) => fragments(
        next(!state.value.path ? {
            loading: true,
        } : (() => {
            const applyEmulation = async (em:Types.Xpresso.Loaders.Emulation) => {
                const path = (
                    em.agentId ? (
                        await apis.next.session.emulateAgent(em)
                    ) : em.isBranchOrder ? (
                        await apis.next.session.emulateBranchOrder({branchId: em.branchId})
                    ) : em.branchId ? (
                        await apis.next.session.emulateBranch({branchId: em.branchId})
                    ) : (
                        await apis.next.session.clearEmulation()
                    )
                );
                state.onChange({path});
                // await dispatchEvent();
                return getValue({path});
            };
            const value = getValue({path: state.value.path});
            return {
                emulated: isEmulated({path: state.value.path, userLevel: userLevels?.value?.userLevel}),

                loading: userLevels.loading,

                // {agentId, branchId, isBranchOrder}
                value: value,

                data: {
                    // {agentName, branchName}
                    ...getNames({path: state.value.path}),
                },
                applyEmulation,
                clearEmulation: () => applyEmulation({}),
                reload: async () => {
                    state.onChange({});
                    const path = await apis.next.session.getUserPath();
                    state.onChange({path});
                },
            };
        })()),
        Invoke({
            action: async () => {
                const path = await apis.next.session.getUserPath();
                state.onChange({path});
            },
        }),
    )],
    ["session", ({session, apis}, next) => cs(
        ...dataLoaders.map(({name, getKey, load}) => (
            [name, ({}, next) => Load({
                _key: session.loading ? "false" : "true|" + getKey(session.value),
                fetch: () => session.loading ? null : load(session.value, {apis}),
                next,
            })]
        )),
        (data) => next(changeNestedValue(session, ["data"], (_) => ({..._, ...data}))),
    )],
    ["tabGuardListenersStatic", ({}, next) => Static2({next})],

    ({apis, session, tabGuardListenersStatic: {set: setTabGuardListeners}}, next) => <>
        {!session.loading && isTabGuardEnabled(userLevels.value?.internalUser) && (
            <TabGuard
                idAgent={Array.isArray(session.data.agent) ? null : session.data.agent?.idAgent}
                idBranch={session.data.branch?.idBranch}
                idCompany={company?.idCompany}
                internalUser={userLevels.value?.internalUser}
                tabListenersRef={setTabGuardListeners}
                userLevel={userLevels.value?.userLevel}
                validateActiveEntity={apis.next.session.validateActiveEntity}
            />
        )}
        {next()}
    </>,

    ({session, tabGuardListenersStatic: {get: getTabGuardListeners}}) => (
        provideContext1("session", {...session, tabGuard: getTabGuardListeners() ?? {}}, next)
    ),
);

const dataLoaders = [
    {
        name: "branch",
        getKey: (value) => value.branchId,
        load: (value, {apis}) => !value.branchId ? null : apis.next.session.getBranch(),
    },
    {
        name: "region",
        getKey: (value) => value.branchId,
        load: (_, {apis}) => apis.next.session.getRegion(),
    },
    {
        name: "agent",
        getKey: (value) => JSON.stringify(value),
        load: (_, {apis}) => apis.next.session.getAgent({}),
    },
    {
        name: "messages",
        getKey: (value) => JSON.stringify(value),
        load: (_, {apis}) => apis.next.session.getMessages(),
    },
];

const getBranchAndAgent = (path) => {
    if (path.length === 0) {
        return {};
    } else if (path.length === 1) {
        return {
            branch: path[0],
        };
    } else {
        const agentIndex = path.findIndex((r) => r.idAgent);
        const agent = path[agentIndex];
        const branch = path[agentIndex === 0 ? 1 : 0];
        return {
            branch,
            agent
        };
    }
};

const getNames = ({path}) => {
    const {branch, agent} = getBranchAndAgent(path);
    return {
        branchName: branch?.branchName,
        agentNameFirst: agent?.agentNameFirst,
        agentNameLast : agent?.agentNameLast,
    };
};

const getValue = ({path}) => {
    const {branch, agent} = getBranchAndAgent(path);
    return {
        branchId: branch?.idBranch,
        ...agent?.idAgent === "Branch Order" ? {
            isBranchOrder: true,
        } : {
            agentId: agent?.idAgent,
        },
    };
};

const isEmulated = ({path, userLevel}) => {
    if (path.length === 0) {
        return false;
    } else if (path.length === 1) {
        return userLevel <= 3;
    } else {
        return userLevel <= 4;
    }
};

// const EventEmitter = {listeners : []};
//
// export const addEventListener = (e) => {
//     EventEmitter.listeners.push(e)
// }
//
// export const removeEventListener = (e) => {
//     EventEmitter.listeners.splice(EventEmitter.listeners.indexOf(e), 1)
// }
//
// const dispatchEvent = async () => {
//     await Promise.all(EventEmitter.listeners.map(async element => {
//         await element();
//     }));
// }
